"use client";

import { Calendar, Copy, ExternalLink, RefreshCw, ShieldCheck, XCircle } from "lucide-react";
import { parseAsArrayOf, parseAsString, useQueryStates } from "nuqs";
import { useMemo, useState } from "react";
import { toast } from "sonner";

import { PageEyebrow } from "@/components/public/page-eyebrow";
import {
  PUBLIC_CONTAINER,
  PUBLIC_PANEL_RADIUS,
  PUBLIC_SECTION_PY_CONTENT,
} from "@/components/public/public-layout-styles";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { dateTimeFormat, longDateFormat, relativeTimeFormat } from "@/lib/date-format";
import { cn } from "@/lib/utils";

type SourceType = "official_news" | "regulation" | "legal_reference";

interface OfficialUpdate {
  _id: string;
  source: string;
  sourceName: string;
  agency: string;
  sourceType: SourceType;
  originalUrl: string;
  title: string;
  excerpt?: string;
  publishedAt: number;
  documentType?: string;
  documentNumber?: string;
  effectiveAt?: number;
  topics: string[];
  priority: number;
}

interface IngestionStatus {
  lastIngestedAt: number | null;
  updateCount: number;
}

interface OfficialSource {
  key: string;
  label: string;
  agency: string;
  sourceType: string;
  url?: string;
}

interface LegislacaoAgroContentProps {
  initialUpdates: OfficialUpdate[];
  ingestionStatus?: IngestionStatus;
  sources: OfficialSource[];
}

const SAO_PAULO_TZ = "America/Sao_Paulo";

const SOURCE_TYPE_LABELS: Record<Exclude<SourceType, "legal_reference">, string> = {
  official_news: "Comunicados oficiais",
  regulation: "Normas e regulação",
};

const TYPE_FILTERS = ["official_news", "regulation"] as const;

const TOPIC_LABELS: Record<string, string> = {
  "plano-safra": "Plano Safra",
  "credito-rural": "Crédito rural",
  pronaf: "Pronaf",
  pronamp: "Pronamp",
  "seguro-rural": "Seguro rural",
  "normas-cmn": "CMN",
  "risco-climatico": "Risco climático",
  sustentabilidade: "Sustentabilidade",
  "politica-agricola": "Política agrícola",
  abastecimento: "Abastecimento",
  paa: "PAA",
};

const EMPTY_TOPICS: string[] = [];

const LEGAL_REFERENCES = [
  {
    label: "Banco Central: busca de normas",
    href: "https://www.bcb.gov.br/estabilidadefinanceira/buscanormas",
  },
  {
    label: "Diário Oficial da União",
    href: "https://www.gov.br/pt-br/servicos/acessar-o-diario-oficial-da-uniao",
  },
  {
    label: "Legislação do Planalto",
    href: "https://www4.planalto.gov.br/legislacao",
  },
] as const;

function getTopicLabel(topic: string): string {
  return TOPIC_LABELS[topic] ?? topic.replace(/-/g, " ");
}

function normalizeTitle(title: string): string {
  return title
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, " ")
    .trim();
}

function dedupeUpdates(updates: OfficialUpdate[]): OfficialUpdate[] {
  const seen = new Set<string>();
  const unique: OfficialUpdate[] = [];

  for (const update of updates) {
    const key = normalizeTitle(update.title);
    if (seen.has(key)) continue;
    seen.add(key);
    unique.push(update);
  }

  return unique;
}

function calendarDay(timestamp: number): string {
  return new Intl.DateTimeFormat("en-CA", {
    timeZone: SAO_PAULO_TZ,
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
  }).format(new Date(timestamp));
}

function shiftIsoDate(isoDate: string, days: number): string {
  const [year, month, day] = isoDate.split("-").map(Number);
  if (year === undefined || month === undefined || day === undefined) {
    return isoDate;
  }
  const shifted = new Date(Date.UTC(year, month - 1, day + days));
  return shifted.toISOString().slice(0, 10);
}

function formatDayHeading(day: string, today: string, yesterday: string): string {
  if (day === today) return "Hoje";
  if (day === yesterday) return "Ontem";
  const [year, month, date] = day.split("-").map(Number);
  if (year === undefined || month === undefined || date === undefined) {
    return day;
  }
  return longDateFormat(new Date(year, month - 1, date));
}

function groupUpdatesByDay(updates: OfficialUpdate[]) {
  const groups: Array<{ day: string; updates: OfficialUpdate[] }> = [];
  for (const update of updates) {
    const day = calendarDay(update.publishedAt);
    const last = groups[groups.length - 1];
    if (last && last.day === day) {
      last.updates.push(update);
    } else {
      groups.push({ day, updates: [update] });
    }
  }
  return groups;
}

function formatRelativeTime(timestamp: number | null | undefined): string {
  if (!timestamp) return "nunca";
  return relativeTimeFormat(timestamp);
}

function matchesSearch(update: OfficialUpdate, term: string): boolean {
  if (!term) return true;
  return (
    update.title.toLowerCase().includes(term) ||
    (update.excerpt ? update.excerpt.toLowerCase().includes(term) : false) ||
    update.sourceName.toLowerCase().includes(term) ||
    update.agency.toLowerCase().includes(term) ||
    update.topics.some((topic) => getTopicLabel(topic).toLowerCase().includes(term))
  );
}

export function LegislacaoAgroContent({
  initialUpdates,
  ingestionStatus,
  sources,
}: LegislacaoAgroContentProps) {
  const [currentTime] = useState(() => Date.now());
  const allSourceKeys = useMemo(() => sources.map((source) => source.key), [sources]);
  const [urlParams, setUrlParams] = useQueryStates(
    {
      search: parseAsString.withDefault(""),
      sources: parseAsArrayOf(parseAsString),
      type: parseAsString.withDefault("all"),
      topics: parseAsArrayOf(parseAsString),
    },
    { history: "replace" },
  );

  const searchTerm = urlParams.search;
  const selectedSources = useMemo(
    () => (urlParams.sources !== null ? urlParams.sources : allSourceKeys),
    [urlParams.sources, allSourceKeys],
  );
  const selectedTopics = urlParams.topics ?? EMPTY_TOPICS;

  const catalogUpdates = useMemo(() => {
    const sorted = [...initialUpdates].sort((a, b) => b.publishedAt - a.publishedAt);
    return dedupeUpdates(sorted);
  }, [initialUpdates]);

  const totalCountsBySource = useMemo(() => {
    const counts: Record<string, number> = Object.fromEntries(allSourceKeys.map((key) => [key, 0]));
    for (const update of catalogUpdates) {
      counts[update.source] = (counts[update.source] ?? 0) + 1;
    }
    return counts;
  }, [allSourceKeys, catalogUpdates]);

  const searchFilteredUpdates = useMemo(() => {
    const term = searchTerm.toLowerCase().trim();
    return catalogUpdates.filter((update) => matchesSearch(update, term));
  }, [catalogUpdates, searchTerm]);

  const sourceCounts = useMemo(() => {
    const counts: Record<string, number> = Object.fromEntries(allSourceKeys.map((key) => [key, 0]));
    for (const update of searchFilteredUpdates) {
      counts[update.source] = (counts[update.source] ?? 0) + 1;
    }
    return counts;
  }, [allSourceKeys, searchFilteredUpdates]);

  const topicCounts = useMemo(() => {
    const counts: Record<string, number> = {};
    for (const update of searchFilteredUpdates) {
      for (const topic of update.topics) {
        counts[topic] = (counts[topic] ?? 0) + 1;
      }
    }
    return counts;
  }, [searchFilteredUpdates]);

  const typeCounts = useMemo(() => {
    const counts: Record<(typeof TYPE_FILTERS)[number], number> = {
      official_news: 0,
      regulation: 0,
    };
    for (const update of searchFilteredUpdates) {
      if (update.sourceType === "official_news" || update.sourceType === "regulation") {
        counts[update.sourceType] += 1;
      }
    }
    return counts;
  }, [searchFilteredUpdates]);

  const filterSources = useMemo(() => {
    if (searchTerm.trim()) return sources;
    return sources.filter((source) => (sourceCounts[source.key] ?? 0) > 0);
  }, [searchTerm, sourceCounts, sources]);

  const filterTopics = useMemo(() => {
    const keys = new Set([...Object.keys(topicCounts), ...selectedTopics]);
    return [...keys]
      .filter((topic) => (topicCounts[topic] ?? 0) > 0 || selectedTopics.includes(topic))
      .sort((a, b) => (topicCounts[b] ?? 0) - (topicCounts[a] ?? 0));
  }, [selectedTopics, topicCounts]);

  const visibleTypeFilters = useMemo(() => {
    return TYPE_FILTERS.filter((type) => typeCounts[type] > 0 || urlParams.type === type);
  }, [typeCounts, urlParams.type]);

  const filteredUpdates = useMemo(() => {
    return searchFilteredUpdates.filter((update) => {
      const matchesSource = selectedSources.includes(update.source);
      const matchesType = urlParams.type === "all" || update.sourceType === urlParams.type;
      const matchesTopic =
        selectedTopics.length === 0 ||
        update.topics.some((topic) => selectedTopics.includes(topic));
      return matchesSource && matchesType && matchesTopic;
    });
  }, [searchFilteredUpdates, selectedSources, selectedTopics, urlParams.type]);

  const setSearchTerm = (value: string) => {
    setUrlParams({ search: value ? value : null });
  };

  const setSelectedSources = (value: string[]) => {
    const isAll =
      value.length === allSourceKeys.length && allSourceKeys.every((key) => value.includes(key));
    setUrlParams({ sources: isAll ? null : value });
  };

  const toggleSource = (key: string) => {
    setSelectedSources(
      selectedSources.includes(key)
        ? selectedSources.filter((source) => source !== key)
        : [...selectedSources, key],
    );
  };

  const toggleTopic = (topic: string) => {
    const next = selectedTopics.includes(topic)
      ? selectedTopics.filter((value) => value !== topic)
      : [...selectedTopics, topic];
    setUrlParams({ topics: next.length > 0 ? next : null });
  };

  const handleCopyLink = async () => {
    try {
      await navigator.clipboard.writeText(window.location.href);
      toast.success("Link copiado com os filtros atuais.");
    } catch {
      toast.error("Não foi possível copiar o link automaticamente.");
    }
  };

  const clearFilters = () => {
    setUrlParams({ search: null, sources: null, type: "all", topics: null });
  };

  const today = calendarDay(currentTime);
  const yesterday = shiftIsoDate(today, -1);
  const dayGroups = groupUpdatesByDay(filteredUpdates);
  const catalogCount = catalogUpdates.length;
  const hasActiveFilters =
    Boolean(searchTerm.trim()) ||
    urlParams.sources !== null ||
    urlParams.type !== "all" ||
    selectedTopics.length > 0;
  const showTypeRow = visibleTypeFilters.length > 1 || urlParams.type !== "all";

  return (
    <main className="relative z-10 pb-16">
      <div className={cn(PUBLIC_CONTAINER, PUBLIC_SECTION_PY_CONTENT, "pb-16")}>
        <div className="mb-10">
          <PageEyebrow className="mb-3" icon={ShieldCheck} variant="emerald">
            Fontes oficiais
          </PageEyebrow>

          <h1 className="mb-3 max-w-[30ch] text-4xl font-semibold tracking-tight text-balance sm:text-5xl">
            Legislação Agro e Plano Safra
          </h1>
          <p className="text-muted-foreground max-w-[48ch] text-lg text-pretty">
            Comunicados e normas oficiais para quem acompanha Plano Safra, crédito rural e política
            agrícola. O texto completo fica no site de origem.
          </p>

          <div className="text-muted-foreground mt-4 flex flex-wrap items-center gap-x-3 gap-y-1 text-sm">
            <div className="flex items-center gap-2">
              <Calendar aria-hidden="true" className="size-4" />
              <span>
                Atualizado{" "}
                {ingestionStatus?.lastIngestedAt ? (
                  <time
                    dateTime={new Date(ingestionStatus.lastIngestedAt).toISOString()}
                    title={dateTimeFormat(ingestionStatus.lastIngestedAt)}
                  >
                    {formatRelativeTime(ingestionStatus.lastIngestedAt)}
                  </time>
                ) : (
                  "aguardando a primeira sincronização"
                )}
              </span>
            </div>
            {catalogCount > 0 ? (
              <>
                <span aria-hidden="true">·</span>
                <span>
                  {catalogCount} {catalogCount === 1 ? "item" : "itens"}
                </span>
              </>
            ) : null}
          </div>
        </div>

        <div className="bg-background sticky top-16 z-10 mb-8 space-y-3 py-3">
          <div className="flex flex-col gap-2 sm:flex-row sm:items-center">
            <div className="relative min-w-0 flex-1">
              <Input
                aria-label="Buscar atualizações oficiais por tema, órgão ou título"
                autoComplete="off"
                // ui-audit-ignore-next-line forms/no-field-size-override: matches the size="sm" filter buttons in this row
                className="h-8 pr-9"
                onChange={(event) => setSearchTerm(event.target.value)}
                placeholder="Buscar por Plano Safra, Pronaf, CMN, crédito rural…"
                spellCheck={false}
                type="search"
                value={searchTerm}
              />
              {searchTerm ? (
                <button
                  aria-label="Limpar busca"
                  className="text-muted-foreground hover:text-foreground focus-visible:ring-ring absolute top-1/2 right-2 -translate-y-1/2 rounded-full p-1 focus:outline-none focus-visible:ring-1"
                  onClick={() => setSearchTerm("")}
                  type="button"
                >
                  <XCircle aria-hidden="true" className="size-4" />
                </button>
              ) : null}
            </div>
            <Button
              aria-label="Copiar link com os filtros atuais"
              className="shrink-0"
              onClick={handleCopyLink}
              size="sm"
              variant="outline"
            >
              <Copy aria-hidden="true" className="size-3.5" />
              Copiar link
            </Button>
          </div>

          {showTypeRow ? (
            <div
              aria-label="Filtrar por tipo de atualização"
              aria-orientation="horizontal"
              className="flex scrollbar-none gap-2 overflow-x-auto pb-1 [-ms-overflow-style:none] sm:flex-wrap sm:overflow-visible sm:pb-0 [&::-webkit-scrollbar]:hidden"
              role="toolbar"
            >
              <Button
                aria-pressed={urlParams.type === "all"}
                className="shrink-0 text-xs whitespace-nowrap"
                onClick={() => setUrlParams({ type: "all" })}
                size="sm"
                variant={urlParams.type === "all" ? "secondary" : "outline"}
              >
                Tudo
              </Button>
              {visibleTypeFilters.map((type) => (
                <Button
                  aria-pressed={urlParams.type === type}
                  className="shrink-0 text-xs whitespace-nowrap"
                  key={type}
                  onClick={() => setUrlParams({ type })}
                  size="sm"
                  variant={urlParams.type === type ? "secondary" : "outline"}
                >
                  {SOURCE_TYPE_LABELS[type]} ({typeCounts[type]})
                </Button>
              ))}
            </div>
          ) : null}

          {filterTopics.length > 0 ? (
            <div
              aria-label="Filtrar por tema"
              aria-orientation="horizontal"
              className="flex scrollbar-none gap-2 overflow-x-auto pb-1 [-ms-overflow-style:none] sm:flex-wrap sm:overflow-visible sm:pb-0 [&::-webkit-scrollbar]:hidden"
              role="toolbar"
            >
              {filterTopics.map((topic) => {
                const isSelected = selectedTopics.includes(topic);
                const count = topicCounts[topic] ?? 0;
                return (
                  <Button
                    aria-pressed={isSelected}
                    className={cn(
                      "shrink-0 text-xs whitespace-nowrap",
                      count === 0 && "opacity-60",
                    )}
                    key={topic}
                    onClick={() => toggleTopic(topic)}
                    size="sm"
                    variant={isSelected ? "secondary" : "outline"}
                  >
                    {getTopicLabel(topic)} ({count})
                  </Button>
                );
              })}
            </div>
          ) : null}

          <div
            aria-label="Filtrar por fontes oficiais"
            aria-orientation="horizontal"
            className="flex scrollbar-none gap-2 overflow-x-auto pb-1 [-ms-overflow-style:none] sm:flex-wrap sm:overflow-visible sm:pb-0 [&::-webkit-scrollbar]:hidden"
            role="toolbar"
          >
            <Button
              aria-label="Selecionar todas as fontes"
              className="shrink-0 text-xs whitespace-nowrap"
              onClick={() => setSelectedSources(allSourceKeys)}
              size="sm"
              variant="outline"
            >
              Todas
            </Button>
            {filterSources.map((source) => {
              const isSelected = selectedSources.includes(source.key);
              const count = sourceCounts[source.key] ?? 0;
              return (
                <Button
                  aria-label={`${isSelected ? "Remover" : "Adicionar"} filtro ${source.label}${count === 0 ? " (sem resultados)" : ""}`}
                  aria-pressed={isSelected}
                  className={cn("shrink-0 text-xs whitespace-nowrap", count === 0 && "opacity-60")}
                  key={source.key}
                  onClick={() => toggleSource(source.key)}
                  size="sm"
                  variant={isSelected ? "secondary" : "outline"}
                >
                  {source.label} ({count})
                </Button>
              );
            })}
          </div>
        </div>

        {filteredUpdates.length === 0 ? (
          catalogUpdates.length === 0 ? (
            <div className={cn("border p-6 text-center", PUBLIC_PANEL_RADIUS)}>
              <div className="bg-muted mx-auto mb-4 flex size-12 items-center justify-center rounded-full">
                <RefreshCw className="text-muted-foreground size-6" />
              </div>
              <h2 className="mb-2 text-lg font-semibold">Primeira sincronização em andamento</h2>
              <p className="text-muted-foreground mx-auto max-w-md text-sm">
                Estamos configurando a ingestão de comunicados oficiais e normas do setor
                agropecuário. As primeiras atualizações devem aparecer em breve.
              </p>
            </div>
          ) : (
            <div className={cn("border p-6 text-center", PUBLIC_PANEL_RADIUS)}>
              <p className="text-muted-foreground">
                Nenhuma atualização encontrada com os filtros atuais.
              </p>
              <Button className="mt-4" onClick={clearFilters} size="sm" variant="outline">
                Limpar filtros
              </Button>
            </div>
          )
        ) : (
          <div>
            {hasActiveFilters && filteredUpdates.length !== catalogCount ? (
              <p aria-live="polite" className="text-muted-foreground mb-3 text-sm">
                {filteredUpdates.length}{" "}
                {filteredUpdates.length === 1 ? "atualização" : "atualizações"}
              </p>
            ) : null}
            {dayGroups.map((group) => (
              <section className="mb-8" key={group.day}>
                <h2 className="text-muted-foreground mb-3 text-sm font-medium tracking-wide uppercase">
                  {formatDayHeading(group.day, today, yesterday)}
                </h2>
                <div
                  className={cn(
                    "border-border/50 divide-border/50 divide-y border",
                    PUBLIC_PANEL_RADIUS,
                  )}
                >
                  {group.updates.map((update) => {
                    const publishedDate = new Date(update.publishedAt);
                    const dateLabel = relativeTimeFormat(publishedDate);
                    const excerpt =
                      update.excerpt && update.excerpt.trim().length > 10 ? update.excerpt : null;

                    return (
                      <article className="p-5 sm:p-6" key={update._id}>
                        <div className="mb-3 flex items-center justify-between gap-3">
                          <Badge variant="outline">{update.agency}</Badge>
                          <time
                            className="text-muted-foreground text-sm tabular-nums"
                            dateTime={publishedDate.toISOString()}
                            title={dateTimeFormat(publishedDate)}
                          >
                            {dateLabel}
                          </time>
                        </div>
                        <h3 className="text-xl font-semibold tracking-tight">
                          <a
                            className="focus-visible:ring-ring rounded-sm underline-offset-4 hover:underline focus-visible:ring-2 focus-visible:outline-none"
                            href={update.originalUrl}
                            rel="noopener noreferrer"
                            target="_blank"
                          >
                            {update.title}
                            <ExternalLink
                              aria-hidden="true"
                              className="text-muted-foreground ml-1.5 inline size-3.5 align-[-2px]"
                            />
                            <span className="sr-only"> (abre no site oficial)</span>
                          </a>
                        </h3>
                        {excerpt ? (
                          <p className="text-muted-foreground mt-3 line-clamp-3 text-base text-pretty">
                            {excerpt}
                          </p>
                        ) : null}
                      </article>
                    );
                  })}
                </div>
              </section>
            ))}
          </div>
        )}

        <div className="text-muted-foreground mt-12 border-t pt-8 text-sm">
          <p className="text-foreground mb-4 font-medium">Fontes monitoradas</p>
          <ul className="grid gap-x-8 gap-y-1 sm:grid-cols-2 md:grid-cols-3">
            {sources.map((source) => {
              const updateCount = totalCountsBySource[source.key] ?? 0;
              return (
                <li key={source.key}>
                  {source.url ? (
                    <a
                      className="hover:text-foreground underline-offset-2 transition-colors hover:underline"
                      href={source.url}
                      rel="noopener noreferrer"
                      target="_blank"
                    >
                      {source.label}
                    </a>
                  ) : (
                    source.label
                  )}
                  <span className="text-muted-foreground ml-1 tabular-nums">({updateCount})</span>
                </li>
              );
            })}
          </ul>

          <p className="text-foreground mt-8 mb-4 font-medium">Referências legais</p>
          <ul className="grid gap-x-8 gap-y-1 sm:grid-cols-2 md:grid-cols-3">
            {LEGAL_REFERENCES.map((reference) => (
              <li key={reference.href}>
                <a
                  className="hover:text-foreground underline-offset-2 transition-colors hover:underline"
                  href={reference.href}
                  rel="noopener noreferrer"
                  target="_blank"
                >
                  {reference.label}
                </a>
              </li>
            ))}
          </ul>

          <div className={cn("bg-surface mt-6 border p-4 text-sm/relaxed", PUBLIC_PANEL_RADIUS)}>
            <strong className="text-foreground">Aviso:</strong> este monitor agrega títulos e
            resumos curtos de páginas oficiais. Ele não substitui consulta jurídica, leitura
            integral do Diário Oficial, do Manual de Crédito Rural ou dos atos normativos originais.
          </div>
        </div>
      </div>
    </main>
  );
}
